import { isPlatformBrowser } from '@angular/common'
import { Injectable, PLATFORM_ID, effect, inject, signal } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'

import { CombinedGraphQLErrors } from '@apollo/client/errors'
import * as Sentry from '@sentry/angular'
import { Apollo } from 'apollo-angular'
import Keycloak from 'keycloak-js'
import type { Observable } from 'rxjs'
import {
  EMPTY,
  catchError,
  filter,
  from,
  map,
  of,
  take,
  tap,
  throwError,
} from 'rxjs'

import { ApiService } from '../../api/api.service'
import type {
  LoginUserDto,
  NotificationOptOutDto,
  ResetPasswordResponseDto,
  UserDto,
} from '../../api/responses'
import {
  AUTH_ERROR_QUERY_PARAM,
  AUTH_NO_CONTENTINTEGRATOR_SEATS,
  AUTH_PROVIDER_QUERY_PARAM,
  KEYCLOAK_IDP_HINT_STORAGE_KEY,
} from './auth.constants'

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly apiService = inject(ApiService)
  private readonly apollo = inject(Apollo)
  private platformId = inject(PLATFORM_ID)
  private readonly keycloak = inject(Keycloak)

  authCheckCompleted = signal(false)
  authCheckCompleted$ = toObservable(this.authCheckCompleted)
  currentUser = signal<UserDto | undefined | null>(undefined)
  currentUser$ = toObservable(this.currentUser)

  constructor() {
    // Keeps Sentry's scope in step with whoever is logged in, so every event,
    // replay and log carries the user and their organization. The feedback
    // widget reads the same scope to prefill its name/email fields - it maps
    // `username` onto the name input, which is why `name` is sent there.
    // `undefined` means the auth check has not resolved yet and must not be
    // mistaken for a logout, which would clear context that was never set.
    effect(() => {
      const user = this.currentUser()
      if (!isPlatformBrowser(this.platformId) || user === undefined) {
        return
      }

      if (!user) {
        Sentry.setUser(null)
        Sentry.setTag('organization_id', undefined)
        return
      }

      Sentry.setUser({
        id: String(user.id),
        email: user.email ?? undefined,
        username: user.name ?? undefined,
      })
      Sentry.setTag(
        'organization_id',
        user.organization?.id != null ? String(user.organization.id) : undefined
      )
    })
  }

  markAuthCheckCompleted(): void {
    this.authCheckCompleted.set(true)
  }

  waitForAuthCheckCompletion(): Observable<void> {
    if (this.authCheckCompleted()) {
      return of(void 0)
    }

    return this.authCheckCompleted$.pipe(
      filter(Boolean),
      take(1),
      map(() => void 0)
    )
  }

  // Login function
  loginUser(username: string, password: string): Observable<LoginUserDto> {
    void this.apollo.client.clearStore()
    return this.apiService.loginUser(username, password).pipe(
      map(response => {
        if (response?.loginUser) {
          this.clearStoredKeycloakIdpHint()
          if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem('token', response.loginUser.token)
          }
          this.currentUser.set(response.loginUser.user)
          return response.loginUser
        }
        throw new Error('Could not validate credentials')
      })
    )
  }

  loginWithKeycloak(idpHint: string, returnUrl = ''): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve()
    }

    this.storeKeycloakIdpHint(idpHint)

    // Login using keycloak with the idpHint and redirect back to the returnUrl after login
    return this.keycloak.login({
      idpHint,
      redirectUri: window.location.origin + returnUrl,
    })
  }

  initKeycloak(): Observable<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return EMPTY
    }
    return from(
      this.keycloak.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri:
          window.location.origin + '/assets/auth-dh.html',
      })
    )
  }

  // Initialize user from token if it exists
  initializeUserFromToken(): Observable<UserDto | null> {
    if (isPlatformBrowser(this.platformId)) {
      const token = this.getToken()
      if (token) {
        return this.refreshUserDetails()
      }
      this.currentUser.set(null)
    }
    return of(null)
  }

  // New method to ensure we can load organization data
  refreshUserDetails(): Observable<UserDto | null> {
    return this.getUserMe(true, false).pipe(
      tap(user => {
        this.currentUser.set(
          user
            ? {
                ...user,
                organization: user.organization
                  ? {
                      ...user.organization,
                    }
                  : user.organization,
              }
            : null
        )
        if (user) {
          this.clearStoredKeycloakIdpHint()
          return
        }

        if (this.isShowingNoSeatsError()) {
          return
        }

        const redirectUri = this.buildNoSeatsRedirectUri()
        this.logout(redirectUri).catch((error: unknown) => {
          console.error('Logout error:', error)
        })
      })
    )
  }

  // Avoid re-triggering the no-seats logout redirect while the resulting
  // error page is already displayed, otherwise a silent SSO re-auth on that
  // page load (check-sso) retriggers this flow, and since the idp hint was
  // already consumed on the first pass, the retry drops the authError query
  // params and closes the dialog it just opened.
  private isShowingNoSeatsError(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false
    }
    return (
      new URLSearchParams(window.location.search).get(
        AUTH_ERROR_QUERY_PARAM
      ) === AUTH_NO_CONTENTINTEGRATOR_SEATS
    )
  }

  // Fetch current user details
  getUserMe(refresh = false, throwError = true): Observable<UserDto | null> {
    return this.apiService
      .getUserMe(refresh ? 'network-only' : 'cache-first')
      .pipe(
        map(response => {
          if (!response.data.meUser.user && throwError) {
            throw new Error('User not found')
          }
          return response.data.meUser.user
        }),
        catchError((error: unknown) => {
          console.error('getUserMe error:', error)
          return of(null)
        })
      )
  }

  // Logout method
  async logout(redirectUri?: string): Promise<void> {
    await this.apollo.client.clearStore()
    this.currentUser.set(null)
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('token')
      if (this.keycloak.authenticated) {
        this.clearStoredKeycloakIdpHint()
        await this.keycloak.logout(redirectUri ? { redirectUri } : undefined)
        return
      }
      this.clearStoredKeycloakIdpHint()
    }
  }

  // Check if the user is authenticated
  isAuthenticated(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      return this.getToken() !== null
    }
    return false
  }

  forgotPassword(email: string): Observable<boolean> {
    return this.apiService.forgotPassword(email).pipe(
      map(result => {
        if (
          result.data &&
          typeof result.data.forgotPasswordUser === 'boolean'
        ) {
          return result.data.forgotPasswordUser
        }
        throw new Error('No data returned from forgotPasswordUser mutation')
      }),
      catchError((error: unknown) => {
        if (CombinedGraphQLErrors.is(error)) {
          console.error('Forgot password error:', error.message)
          return throwError(
            () => new Error('Could not process forgot password request')
          )
        }
        return throwError(() => error)
      })
    )
  }

  resetPassword(
    password: string,
    token: string
  ): Observable<ResetPasswordResponseDto | null | undefined> {
    return this.apiService
      .resetPassword(password, token)
      .pipe(map(result => result.data))
  }

  updateUser(partialUser: Partial<UserDto>): Observable<UserDto | null> {
    const userId = this.currentUser()?.id
    if (typeof userId !== 'number') {
      return of(null)
    }
    return this.apiService
      .updateUser(userId, partialUser)
      .pipe(map(result => result.data?.updateUser ?? null))
  }

  getNotificationOptOuts(): Observable<NotificationOptOutDto[]> {
    return this.apiService
      .getNotificationOptOuts()
      .pipe(map(result => result.data?.MagazineNotificationOptOuts.docs ?? []))
  }

  createNotificationOptOut(
    magazineId: number
  ): Observable<NotificationOptOutDto | null> {
    return this.apiService
      .createNotificationOptOut(magazineId)
      .pipe(
        map(result => result.data?.createMagazineNotificationOptOut ?? null)
      )
  }

  deleteNotificationOptOut(id: number): Observable<number | null> {
    return this.apiService
      .deleteNotificationOptOut(id)
      .pipe(
        map(result => result.data?.deleteMagazineNotificationOptOut.id ?? null)
      )
  }

  private getToken(): string | null {
    const tokens = []

    tokens.push(this.keycloak.token)
    try {
      tokens.push(localStorage.getItem('token'))
    } catch {
      // Ignore localStorage errors
    }

    return tokens.filter(Boolean).shift() ?? null
  }

  private buildNoSeatsRedirectUri(): string | undefined {
    if (!isPlatformBrowser(this.platformId)) {
      return undefined
    }

    const storedKeycloakIdpHint = this.getStoredKeycloakIdpHint()
    if (!storedKeycloakIdpHint) {
      return undefined
    }

    const redirectUrl = new URL('/', window.location.origin)

    redirectUrl.searchParams.set(
      AUTH_ERROR_QUERY_PARAM,
      AUTH_NO_CONTENTINTEGRATOR_SEATS
    )
    redirectUrl.searchParams.set(
      AUTH_PROVIDER_QUERY_PARAM,
      storedKeycloakIdpHint
    )

    return redirectUrl.toString()
  }

  private storeKeycloakIdpHint(idpHint: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return
    }

    try {
      window.sessionStorage.setItem(KEYCLOAK_IDP_HINT_STORAGE_KEY, idpHint)
    } catch {
      // Ignore sessionStorage errors
    }
  }

  private getStoredKeycloakIdpHint(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null
    }

    try {
      return window.sessionStorage.getItem(KEYCLOAK_IDP_HINT_STORAGE_KEY)
    } catch {
      return null
    }
  }

  private clearStoredKeycloakIdpHint(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return
    }

    try {
      window.sessionStorage.removeItem(KEYCLOAK_IDP_HINT_STORAGE_KEY)
    } catch {
      // Ignore sessionStorage errors
    }
  }
}
